'use client';

import customFetch from '@/api/fetch';
import { useTranslation } from '@/app/i18n/client';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthV2 } from '@/hooks/useAuthV2';
import { formatCurrency } from '@/utils/currency';
import { setSessionStorage } from '@/utils/session-storage';
import { EnumExperienceType, EnumMaspType, IPlan, UserGroups } from '@/utils/types';
import { motion } from 'framer-motion';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ArrowRight } from 'lucide-react';
import { useParams, useRouter } from 'next/navigation';
import { Fragment, useEffect, useRef, useState, useTransition } from 'react';

interface AmigoMaspClientProps {
  initialPlans?: IPlan[];
}

export default function AmigoMaspClient({ initialPlans }: AmigoMaspClientProps) {
  const { lng, refer } = useParams() as { lng: string; refer?: string };
  const { t } = useTranslation(lng, ['payment']);
  const { amigoMaspRelease, authenticateUsers } = useFlags();

  const [plans, setPlans] = useState<IPlan[]>(initialPlans || []);
  const [isLoading, startTransition] = useTransition();
  const [openPlanId, setOpenPlanId] = useState<string | null>(null);

  const { user, userGroups, userLoaded } = useAuthV2();
  const router = useRouter();

  useEffect(() => {
    if (refer) {
      // Amigo MASP Indication token
      setSessionStorage('refer', refer);
    }
  }, [refer]);

  const handleGetPlans = async () => {
    startTransition(async () => {
      try {
        const response = await customFetch(
          `/plans?experienceType=${EnumExperienceType.MASP_AMIGO}&planTypeName=${EnumMaspType.AMIGO_MASP}`,
          { method: 'GET' }
        );
        console.log(response.plans);
        setPlans(response.plans);
      } catch (err) {
        console.error(err);
      }
    });
  };

  const handleCheckoutRedirect = (planId: string, v = false) => {
    if (v) {
      router.push('/subscription-checkout?v=true');
    } else {
      router.push(`/subscription-checkout?plan=${planId}`);
    }
  };

  const handleRedeemFriendVoucherClick = () => {
    if (user) {
      router.push('/redeem-subscription');
      return;
    }

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const redirectTarget = `${origin}/redeem-subscription`;
    router.push(`/login?redirectURI=${encodeURIComponent(redirectTarget)}`);
  };

  const adminRefreshDone = useRef(false);

  useEffect(() => {
    if (!initialPlans?.length) {
      handleGetPlans();
    }
  }, []);

  useEffect(() => {
    if (!userLoaded) return;
    const isStaff = userGroups.some((g) => [UserGroups.STAFF, UserGroups.ADMIN].includes(g as UserGroups));
    if (!isStaff || adminRefreshDone.current) return;
    adminRefreshDone.current = true;

    const refreshForAdmin = async () => {
      try {
        const response = await customFetch(
          `/plans?experienceType=${EnumExperienceType.MASP_AMIGO}&planTypeName=${EnumMaspType.AMIGO_MASP}`,
          { method: 'GET' }
        );
        console.log(response.plans);
        setPlans(response.plans);
      } catch (err) {
        console.error('[AdminRefresh] Failed to refresh plans:', err);
      }
    };
    refreshForAdmin();
  }, [userLoaded, userGroups]);

  useEffect(() => {
    if (amigoMaspRelease === false) router.push('/404');
  }, [amigoMaspRelease]);

  // Kill-switch: esconde apenas quando o LD respondeu `false` explicitamente.
  // `undefined` (SSR, LD bloqueado/lento — ex.: webview do Instagram) renderiza normalmente.
  if (amigoMaspRelease === false) return null;

  return (
    <div data-testid='amigo-masp-page' className='mx-auto flex w-full flex-col items-center justify-center p-10'>
      {!isLoading && plans.length > 0 ? (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className='flex w-full items-center justify-start space-y-2 bg-primary p-10 px-0 text-white'>
          <div className='ml-6 space-y-2 max-sm:m-0'>
            <h1 className='font-medium max-sm:px-4 max-sm:text-center max-sm:text-lg'>{t('amigoMaspHeaderTitle')}</h1>
          </div>
        </motion.div>
      ) : (
        <Skeleton className='h-36 w-full p-10' />
      )}

      <div className='mt-10 grid w-full grid-cols-3 items-center justify-center gap-10 max-[1140px]:grid-cols-2 max-sm:grid-cols-1'>
        {plans.map(({ title, id, price, description, benefits, maxInstallments }, index) => (
          <Fragment key={`${id}-wrapper`}>
            <div
              data-testid='plan-card'
              className='group flex h-full w-full flex-col justify-between space-y-4 border bg-zinc-300/10 p-10 transition-all duration-300 hover:border-primary-400 hover:bg-primary-50 hover:shadow-lg'>
              <div className='space-y-2'>
                <h1 className='text-xl font-medium text-primary'>{title}</h1>
                <p className='text-4xl font-normal text-foreground'>
                  {maxInstallments}x {formatCurrency(Math.round(price / maxInstallments))}
                </p>
              </div>

              <p className='text-zinc-700 max-sm:text-sm'>{description}</p>
              <p className='text-sm text-zinc-500'>Valor total: {formatCurrency(price)}</p>

              <Dialog open={openPlanId === id} onOpenChange={(isOpen) => setOpenPlanId(isOpen ? id : null)}>
                <DialogTrigger asChild>
                  <Button variant='outline-primary' className='py-4'>
                    {t('amigoMaspButton')}
                    <ArrowRight className='ml-2 h-5 w-5' />
                  </Button>
                </DialogTrigger>
                <DialogContent className='w-full max-w-4xl'>
                  <div className='max-h-[80vh] overflow-auto p-4'>
                    <div className='flex items-center justify-between border-b pb-6 max-sm:flex-col max-sm:items-start max-sm:gap-4'>
                      <div className='space-y-4'>
                        <h1 className='text-xl font-medium text-primary'>{title}</h1>
                        <p className='text-4xl font-normal text-foreground'>{formatCurrency(price)}</p>
                      </div>

                      <Button
                        variant='primary'
                        className='py-4'
                        onClick={() => {
                          setOpenPlanId(null);
                          handleCheckoutRedirect(id, false);
                        }}>
                        {t('amigoMaspButton2')}
                        <ArrowRight className='ml-2 h-5 w-5' />
                      </Button>
                    </div>

                    {Object.entries(JSON.parse(benefits)).map(([category, benefitsList]) => (
                      <div key={`${id}-${category}`}>
                        <span className='mb-1 mt-6 inline-flex font-medium text-foreground'>{category}</span>
                        <ul>
                          {Array.isArray(benefitsList) ? (
                            benefitsList.map((benefit: any, idx: number) => {
                              // Novo modelo: objeto com name, location (opcional), description (opcional)
                              if (typeof benefit === 'object' && benefit !== null && 'name' in benefit) {
                                const location = benefit.location || benefit.local;
                                return (
                                  <li key={`${id}-${category}-${idx}`} className='flex gap-2 text-zinc-700'>
                                    <div className='flex flex-col'>
                                      <span>{benefit.name}</span>
                                      {location && <span className='text-sm text-zinc-500'>{location}</span>}
                                      {benefit.description && (
                                        <span className='text-sm text-zinc-500'>{benefit.description}</span>
                                      )}
                                    </div>

                                    {Array.isArray(benefit?.coupons) && benefit?.coupons?.length > 0 && (
                                      <div className='mt-1 flex flex-wrap gap-1 border-l border-black/10 pl-4'>
                                        {benefit?.coupons?.map((code: string, i: number) => (
                                          <div
                                            key={i}
                                            className='h-fit rounded border bg-foreground px-2 py-1 text-xs font-medium tracking-wide text-secondary'>
                                            {code}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </li>
                                );
                              }
                              // Modelo antigo: string simples
                              return (
                                <li key={`${id}-${category}-${idx}`} className='flex text-zinc-700'>
                                  <span>{String(benefit)}</span>
                                </li>
                              );
                            })
                          ) : (
                            <li className='flex text-zinc-700'>
                              <span>{String(benefitsList)}</span>
                            </li>
                          )}
                        </ul>
                      </div>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {index === 1 && plans.length > 0 && (
              <div
                key='voucher-card'
                className='group flex h-full w-full flex-col justify-between space-y-4 border bg-zinc-300/10 p-10 transition-all duration-300 hover:border-primary-400 hover:bg-primary-50 hover:shadow-lg'>
                <div>
                  <h1 className='mb-4 text-xl font-medium text-primary'>{t('voucherTitle')}</h1>
                  <p className='h-full max-h-60 min-h-56 overflow-auto text-zinc-700 max-sm:text-sm'>
                    {t('voucherDescription')}
                  </p>
                </div>
                <Button variant='primary' className='py-4' onClick={handleRedeemFriendVoucherClick}>
                  {t('redeemFriendVoucher')}
                  <ArrowRight className='ml-2 h-5 w-5' />
                </Button>
                <Button
                  variant='outline-primary'
                  className='py-4'
                  onClick={() => handleCheckoutRedirect(plans[0].id, true)}>
                  {t('voucherCTA')}
                  <ArrowRight className='ml-2 h-5 w-5' />
                </Button>
              </div>
            )}
          </Fragment>
        ))}

        {(isLoading || !plans.length) &&
          Array.from({ length: 5 }).map((_, index) => <Skeleton key={index} className='h-96 w-full p-10' />)}
      </div>
    </div>
  );
}
